import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, Sparkles, X as XIcon, Power, AlertCircle, FileText } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import AutoTextarea from "./AutoTextarea";

/**
 * ProtocolTemplatesEditor (Impostazioni Medico → tab "Protocolli per diagnosi")
 * - Lista template protocolli del medico (per tenant).
 * - CRUD: crea, modifica, attiva/disattiva, elimina.
 * - "Genera protocollo IA" → bozza modificabile, sources non inventati (linee guida ufficiali o "Fonte da verificare").
 * - Hashtag normalizzati lato server.
 */

function normHashtag(raw) {
  let t = String(raw || "").trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
  if (!t) return "";
  if (!t.startsWith("#")) t = "#" + t;
  return t.replace(/[^#a-z0-9_]/g, "").slice(0, 40);
}

export default function ProtocolTemplatesEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [baseText, setBaseText] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Form nuovo / generate
  const [draft, setDraft] = useState({
    hashtag: "",
    pathology: "",
    synonyms_raw: "",
    protocol_text: "",
    sources_raw: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/medico/protocol-templates");
      setItems(data.items || []);
      setBaseText(data.base_text || "");
    } catch (e) {
      toast.error(apiErr(e, "Errore caricamento protocolli"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startBlank = () => {
    setDraft({ hashtag: "", pathology: "", synonyms_raw: "", protocol_text: baseText, sources_raw: "" });
    setShowNew(true);
  };

  const startGenerate = async () => {
    const pathology = window.prompt("Per quale patologia generare il protocollo?\n(es. Pterigio, Glaucoma sospetto, Maculopatia secca)");
    if (!pathology || !pathology.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/medico/protocol-templates/generate", { pathology: pathology.trim() });
      setDraft({
        hashtag: data.hashtag || "",
        pathology: data.pathology || pathology.trim(),
        synonyms_raw: (data.synonyms || []).join(", "),
        protocol_text: data.protocol_text || "",
        sources_raw: (data.sources || []).join("\n"),
      });
      setShowNew(true);
      if (data.warning) toast.info(data.warning, { duration: 8000 });
    } catch (e) {
      toast.error(apiErr(e, "Errore generazione IA"));
    } finally {
      setGenerating(false);
    }
  };

  const saveNew = async () => {
    const hashtag = normHashtag(draft.hashtag);
    if (!hashtag || hashtag === "#") return toast.error("Hashtag richiesto");
    if (!draft.protocol_text.trim()) return toast.error("Testo protocollo richiesto");
    setSavingId("new");
    try {
      const payload = {
        hashtag,
        pathology: draft.pathology,
        synonyms: draft.synonyms_raw.split(",").map((s) => s.trim()).filter(Boolean),
        protocol_text: draft.protocol_text,
        sources: draft.sources_raw.split("\n").map((s) => s.trim()).filter(Boolean),
        active: true,
      };
      await api.post("/medico/protocol-templates", payload);
      toast.success("Protocollo creato");
      setShowNew(false);
      setDraft({ hashtag: "", pathology: "", synonyms_raw: "", protocol_text: "", sources_raw: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e, "Errore creazione"));
    } finally {
      setSavingId(null);
    }
  };

  const updateItem = async (it, patch) => {
    setSavingId(it.id);
    try {
      const payload = { ...it, ...patch };
      payload.synonyms = Array.isArray(payload.synonyms) ? payload.synonyms : (payload.synonyms_raw || "").split(",").map((s) => s.trim()).filter(Boolean);
      payload.sources = Array.isArray(payload.sources) ? payload.sources : (payload.sources_raw || "").split("\n").map((s) => s.trim()).filter(Boolean);
      delete payload.synonyms_raw;
      delete payload.sources_raw;
      await api.put(`/medico/protocol-templates/${it.id}`, payload);
      toast.success("Aggiornato");
      load();
    } catch (e) {
      toast.error(apiErr(e, "Errore aggiornamento"));
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = (it) => updateItem(it, { active: !it.active });

  const remove = async (it) => {
    if (!window.confirm(`Eliminare il protocollo ${it.hashtag}?`)) return;
    setDeletingId(it.id);
    try {
      await api.delete(`/medico/protocol-templates/${it.id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error(apiErr(e, "Errore eliminazione"));
    } finally {
      setDeletingId(null);
    }
  };

  const onChangeField = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  return (
    <div className="space-y-4" data-testid="protocol-templates-editor">
      <div className="bg-brand-azure/5 border border-brand-azure/30 rounded-xl p-4 text-xs text-brand-navy">
        <b>Protocolli per diagnosi / hashtag.</b> I template definiscono il testo automatico che viene proposto in cartella quando una diagnosi corrisponde all'hashtag.
        Sono <b>privati</b>: nessun altro medico li vede.
        Le fonti sono nomi di linee guida ufficiali verificabili (AAO, EGS, TFOS DEWS II, IMI, EURETINA, ICO, SIO). Nessun DOI o link inventato. Modifica liberamente prima di salvare.
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={startBlank}
          data-testid="protocol-tpl-new"
          className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"
        >
          <Plus size={14} /> Nuovo protocollo
        </button>
        <button
          type="button"
          onClick={startGenerate}
          disabled={generating}
          data-testid="protocol-tpl-generate"
          className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-60"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          Genera protocollo (IA)
        </button>
      </div>

      {/* Modal nuovo / generato */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-extrabold text-brand-navy text-lg">Nuovo protocollo</h3>
              <button onClick={() => setShowNew(false)}><XIcon size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Hashtag*</label>
                <input
                  type="text"
                  value={draft.hashtag}
                  onChange={(e) => setDraft({ ...draft, hashtag: e.target.value })}
                  placeholder="#pterigio"
                  data-testid="protocol-tpl-new-hashtag"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-mono"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Patologia / diagnosi</label>
                <input
                  type="text"
                  value={draft.pathology}
                  onChange={(e) => setDraft({ ...draft, pathology: e.target.value })}
                  placeholder="Pterigio nasale"
                  data-testid="protocol-tpl-new-pathology"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sinonimi / trigger (separati da virgola)</label>
                <input
                  type="text"
                  value={draft.synonyms_raw}
                  onChange={(e) => setDraft({ ...draft, synonyms_raw: e.target.value })}
                  placeholder="pterigio, pterygium, neoformazione fibrovascolare"
                  data-testid="protocol-tpl-new-synonyms"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Testo protocollo*</label>
                <AutoTextarea
                  value={draft.protocol_text}
                  onChange={(v) => setDraft({ ...draft, protocol_text: v })}
                  placeholder="Spiegato al paziente…"
                  data-testid="protocol-tpl-new-text"
                  minRows={8}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm leading-relaxed"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Fonti scientifiche (una per riga)</label>
                <AutoTextarea
                  value={draft.sources_raw}
                  onChange={(v) => setDraft({ ...draft, sources_raw: v })}
                  placeholder="American Academy of Ophthalmology — Preferred Practice Pattern: Conjunctivitis"
                  data-testid="protocol-tpl-new-sources"
                  minRows={3}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm"
                />
                <div className="text-[10px] text-slate-400 mt-1 italic flex items-center gap-1">
                  <AlertCircle size={10} /> Solo nomi di linee guida ufficiali verificabili. Per fonti non verificabili scrivi "Fonte da verificare".
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:bg-slate-100">Annulla</button>
              <button
                onClick={saveNew}
                disabled={savingId === "new"}
                data-testid="protocol-tpl-new-save"
                className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-60"
              >
                {savingId === "new" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salva protocollo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center text-slate-500">
          <Loader2 className="animate-spin mx-auto mb-2" /> Caricamento protocolli…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 text-center text-slate-500 text-sm">
          Nessun protocollo configurato. Clicca su "Nuovo protocollo" o "Genera protocollo (IA)" per iniziare.
        </div>
      ) : (
        <div className="space-y-3" data-testid="protocol-tpl-list">
          {items.map((it, idx) => (
            <div
              key={it.id}
              className={`bg-white rounded-xl border ${it.active ? "border-[#E2E8F0]" : "border-slate-200 opacity-60"} p-4`}
              data-testid={`protocol-tpl-item-${it.hashtag.replace(/[^a-z0-9]/gi, "")}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      value={it.hashtag}
                      onChange={(e) => onChangeField(idx, "hashtag", e.target.value)}
                      data-testid={`protocol-tpl-${it.id}-hashtag`}
                      className="text-base font-extrabold text-brand-navy font-mono px-2 py-1 rounded border border-transparent hover:border-[#E2E8F0] focus:border-brand-azure focus:outline-none"
                    />
                    {!it.active && <span className="text-[10px] uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded">Disattivato</span>}
                    {it.is_default_seed && <span className="text-[10px] uppercase tracking-wider text-brand-azure bg-brand-azure/10 px-2 py-0.5 rounded">Predefinito</span>}
                  </div>
                  <input
                    type="text"
                    value={it.pathology || ""}
                    onChange={(e) => onChangeField(idx, "pathology", e.target.value)}
                    placeholder="Patologia"
                    data-testid={`protocol-tpl-${it.id}-pathology`}
                    className="mt-1 text-sm text-slate-700 px-2 py-1 rounded border border-transparent hover:border-[#E2E8F0] focus:border-brand-azure focus:outline-none w-full"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleActive(it)}
                    title={it.active ? "Disattiva" : "Attiva"}
                    data-testid={`protocol-tpl-${it.id}-toggle`}
                    className={`p-1.5 rounded ${it.active ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}
                  >
                    <Power size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(it)}
                    disabled={deletingId === it.id}
                    title="Elimina"
                    data-testid={`protocol-tpl-${it.id}-delete`}
                    className="p-1.5 rounded text-red-500 hover:bg-red-50 disabled:opacity-60"
                  >
                    {deletingId === it.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  </button>
                </div>
              </div>

              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sinonimi (virgola)</label>
                <input
                  type="text"
                  value={(it.synonyms || []).join(", ")}
                  onChange={(e) => onChangeField(idx, "synonyms", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  data-testid={`protocol-tpl-${it.id}-synonyms`}
                  className="w-full mt-1 px-2 py-1.5 rounded border border-[#E2E8F0] text-xs"
                />
              </div>

              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Testo protocollo</label>
                <AutoTextarea
                  value={it.protocol_text || ""}
                  onChange={(v) => onChangeField(idx, "protocol_text", v)}
                  data-testid={`protocol-tpl-${it.id}-text`}
                  minRows={6}
                  className="w-full mt-1 px-3 py-2 rounded border border-[#E2E8F0] text-sm leading-relaxed"
                />
              </div>

              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
                  <FileText size={11} /> Fonti scientifiche (una per riga)
                </label>
                <AutoTextarea
                  value={(it.sources || []).join("\n")}
                  onChange={(v) => onChangeField(idx, "sources", v.split("\n").map((s) => s.trim()).filter(Boolean))}
                  placeholder="American Academy of Ophthalmology — Preferred Practice Pattern…"
                  data-testid={`protocol-tpl-${it.id}-sources`}
                  minRows={2}
                  className="w-full mt-1 px-3 py-2 rounded border border-[#E2E8F0] text-xs leading-relaxed"
                />
              </div>

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => updateItem(it, {
                    hashtag: it.hashtag,
                    pathology: it.pathology,
                    synonyms: Array.isArray(it.synonyms) ? it.synonyms : [],
                    protocol_text: it.protocol_text,
                    sources: Array.isArray(it.sources) ? it.sources : [],
                    active: it.active,
                  })}
                  disabled={savingId === it.id}
                  data-testid={`protocol-tpl-${it.id}-save`}
                  className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-60"
                >
                  {savingId === it.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Salva modifiche
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
